// @flow
import React, {useState, useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Card,
  Divider,
  Collapse,
  IconButton,
} from '@material-ui/core';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';
import {ExpandMore, ExpandLess} from '@material-ui/icons';
import {Check, Delete, Edit} from '@material-ui/icons';
import {useHistory} from 'react-router-dom';
import DeleteConfirmationModal from '../../../../../products/components/DeleteConfirmationModal/DeleteConfirmationModal';
import {useCRUDAdvancedRate, useGetCurrencyCountry} from '../../utils';
import {
  fetchAdmin1,
  fetchAdmin2,
  getConfigurationAdmin,
} from '../../../../../../utils/location';
import ConfigCountryModal from '../../components/ConfigCountryModal';
import type {Admin1Type} from '../../../../../../utils/location/api';
import type {ShippingAdvancedRate} from '../../../../../../entities';

type Classes = {
  collapsible: string,
  header: string,
  textHeader: string,
  check: string,
  bold: string,
  buttonsHeader: string,
  editButton: string,
  card: string,
  containerDataRow: string,
  containerData: string,
  dataBetween: string,
  textGrey: string,
  dotLine: string,
  textGreyBold: string,
  dividerDetails: string,
  deleteButton: string,
  textGrey2: string,
  notCheck: string,
  containerDataLocation: string,
};

type Props = {
  rate: ShippingAdvancedRate,
  classes: Classes,
  isNational: boolean,
  isEditable: boolean,
};

export default function CollapsibleAdvancedShipping(props: Props) {
  const history = useHistory();
  const {rate, classes, isNational, isEditable} = props;
  const [collapsed, setCollapsed] = useState(false);
  const [showModalDeleteRate, setShowModalDeleteRate] = useState(false);
  const {
    isSaving,
    removeAdvancedRate,
    updateAdvancedRate,
  } = useCRUDAdvancedRate();

  const currency = useGetCurrencyCountry();
  const [showModalConfigCountry, setShowModalConfigCountry] = useState(false);
  const [admin1Ids, setAdmin1Ids] = useState<string[]>([]);
  const [admin1List, setAdmin1List] = useState<Admin1Type[]>([]);
  const [dataAdmins, setDataAdmins] = useState([]);

  const handleModalDeleteClose = () => {
    setShowModalDeleteRate(false);
  };

  const handleDeleteClick = () => {
    setShowModalDeleteRate(false);
    removeAdvancedRate(rate.id);
  };

  const handleChangeEnabled = () => {
    const formattedRateRequest = {
      enabled: !rate.enabled,
    };

    updateAdvancedRate({
      advancedRateId: rate.id,
      advancedRate: formattedRateRequest,
    });
  };

  useEffect(() => {
    if (!collapsed) return;
    if (!rate) return;
    if (admin1List.length !== 0) return;

    const countries = Object.keys(rate.location);
    Promise.all(countries.map(country => fetchAdmin1(country))).then(
      results => {
        // $FlowFixMe
        setAdmin1List(results.flat());
      }
    );
  }, [admin1List.length, collapsed, rate]);

  useEffect(() => {
    if (dataAdmins.length !== 0) return;
    if (!collapsed) return;
    if (!rate) return;

    const {location} = rate;
    const countries = Object.keys(location);

    Promise.all(
      countries.flatMap(country => {
        const admin1s = location[country];
        setAdmin1Ids(Object.keys(admin1s));
        return admin1Ids.map(admin1Id => fetchAdmin2(country, admin1Id));
      })
    ).then(admin2Lists => {
      const dataAdmins = countries
        .flatMap((country, countryIndex) => {
          const admin1s = location[country];
          return Object.entries(admin1s).map(([admin1Id, value], index) => {
            const matchingAdmin1Item = admin1List.find(
              item => String(item.admin1Id) === String(admin1Id)
            );
            const admin2List =
              admin2Lists[countryIndex * Object.keys(admin1s).length + index];
            if (matchingAdmin1Item) {
              let children = [];
              if (typeof value === 'object') {
                // $FlowFixMe
                children = Object.keys(value).map(admin2Id => {
                  const matchingAdmin2Item = admin2List.find(
                    item => String(item.admin2Id) === String(admin2Id)
                  );
                  return matchingAdmin2Item
                    ? {
                        admin2Id: matchingAdmin2Item.admin2Id,
                        adminName: matchingAdmin2Item.admin2Name,
                      }
                    : null;
                });
              } else if (typeof value === 'boolean' && value) {
                children = admin2List.map(item => ({
                  admin2Id: item.admin2Id,
                  adminName: item.admin2Name,
                }));
              }
              return {
                admin1Id: matchingAdmin1Item.admin1Id,
                adminName: matchingAdmin1Item.admin1Name,
                children: children,
              };
            }
            return null;
          });
        })
        .filter(Boolean);

      // $FlowFixMe
      setDataAdmins(dataAdmins);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rate, admin1List, dataAdmins.length, collapsed]);

  const adminConfig = useMemo(() => {
    if (!rate) return getConfigurationAdmin('PE');
    const countryKeys = Object.keys(rate.location || {});
    const countryCode = countryKeys[0];
    return getConfigurationAdmin(countryCode);
  }, [rate]);

  console.log({adminConfig});

  return (
    <>
      <div className={classes.collapsible}>
        <div className={classes.header}>
          <div className={classes.textHeader}>
            <button
              className={rate.enabled ? classes.check : classes.notCheck}
              type="button"
              onClick={handleChangeEnabled}
              disabled={isSaving || !isEditable}
            >
              {rate.enabled ? <Check /> : null}
            </button>
            <Typography align="left" className={classes.bold}>
              {rate.label}
            </Typography>
          </div>
          <div className={classes.buttonsHeader}>
            <CustomButton
              className={classes.editButton}
              variant="text"
              startIcon={<Edit />}
              onClick={() => {
                if (isEditable) {
                  history.push(
                    `/configure/logistics/shipping/advanced/${
                      isNational ? 'national' : 'international'
                    }/${rate.id}`
                  );
                } else {
                  setShowModalConfigCountry(true);
                }
              }}
            >
              Editar
            </CustomButton>
            <Divider orientation="vertical" flexItem />
            <Typography className={`${classes.textGrey2} ${classes.bold}`}>
              {collapsed ? 'Ocultar detalles' : 'Ver detalles'}
            </Typography>
            <IconButton size="small" onClick={() => setCollapsed(!collapsed)}>
              {!collapsed ? <ExpandMore /> : <ExpandLess />}
            </IconButton>
          </div>
        </div>
        <Collapse in={collapsed}>
          <Card variant="outlined" className={classes.card}>
            <div className={classes.containerDataRow}>
              <div className={classes.containerData}>
                {rate.shippingRates.map((shippingRate, index) => (
                  <div key={index}>
                    <Typography align="left" className={classes.textGreyBold}>
                      Tarifa N° {index + 1}
                    </Typography>
                    <div className={classes.dataBetween}>
                      <Typography align="left" className={classes.textGrey}>
                        Nombre de la tarifa:
                      </Typography>
                      <hr className={classes.dotLine} />
                      <Typography align="left" className={classes.textGreyBold}>
                        {shippingRate.name}
                      </Typography>
                    </div>
                    <div className={classes.dataBetween}>
                      <Typography align="left" className={classes.textGrey}>
                        Costo de envío:
                      </Typography>
                      <hr className={classes.dotLine} />
                      <Typography align="left" className={classes.textGreyBold}>
                        {currency.symbol} {shippingRate.price}
                      </Typography>
                    </div>
                    {index !== rate.shippingRates.length - 1 && (
                      <Divider className={classes.dividerDetails} />
                    )}
                  </div>
                ))}
                {/* Description */}
                {/* <div className={classes.dataBetween}>
                  <Typography align="left" className={classes.textGrey}>
                    Descripción:
                  </Typography>
                  <hr className={classes.dotLine} />
                  <Typography align="left" className={classes.textGreyBold}>
                    {rate.message}
                  </Typography>
                </div> */}
                <Divider className={classes.dividerDetails} />
                <div>
                  <div className={classes.containerDataLocation}>
                    {dataAdmins.length > 0 &&
                      dataAdmins.map(admin1 => (
                        <div key={admin1.admin1Id}>
                          <Typography
                            align="left"
                            className={classes.textGreyBold}
                          >
                            {adminConfig.admin1.label}: {admin1.adminName}
                          </Typography>
                          <div>
                            <Typography
                              align="left"
                              className={classes.textGrey}
                            >
                              {adminConfig.admin2.label}:{' '}
                              {admin1.children
                                .map(child => child.adminName)
                                .join(', ')}
                            </Typography>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              <Divider orientation="vertical" flexItem />
              <div className={classes.containerData}>
                <Typography
                  align="left"
                  className={classes.textGrey}
                  style={{
                    marginBottom: '10px',
                  }}
                >
                  Mensaje:
                </Typography>

                <Typography align="left" className={classes.textGrey}>
                  {rate.description}
                </Typography>
              </div>
            </div>
            <CustomButton
              className={classes.deleteButton}
              variant="text"
              startIcon={<Delete />}
              onClick={() => setShowModalDeleteRate(true)}
            >
              Eliminar
            </CustomButton>
          </Card>
        </Collapse>
      </div>

      <DeleteConfirmationModal
        title="¿Seguro que quieres eliminar esta tarifa?"
        content="Si lo eliminas no lo podrás recuperar."
        open={showModalDeleteRate}
        handleClose={handleModalDeleteClose}
        handleDeleteClick={handleDeleteClick}
        disabled={isSaving}
      />

      <ConfigCountryModal
        open={showModalConfigCountry}
        handleClose={() => {
          setShowModalConfigCountry(false);
        }}
      />
    </>
  );
}

CollapsibleAdvancedShipping.propTypes = {
  rate: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  isNational: PropTypes.bool.isRequired,
  isEditable: PropTypes.bool.isRequired,
};
